/* eslint-disable no-undefined */
// import classNames from 'classnames';
import { useState, useContext, useEffect, useMemo } from 'preact/hooks';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import scorePredictorStyleSheet from './styles/scorePredictor.module.scss';
import styleSheet from './styles/results.module.scss';

import WinnerIcon from '../../assets/icons/WinnerIcon';

import {
    isPredictionCorrect,
    isFanPredictionMatch,
    getVotePercentage
} from '../helpers/scorePredictor.helpers';

import { getImageUrl, onErrorImage } from '../../helpers/images';

import SubmitButton from '../../components/shared-components/button/SubmitButton';
import ResultsBadge from './resultsBadge.component';

import { ScorePredictorContext } from './ScorePredictorContent.component';

const styles = styleSheet.locals || {};
const scorePredictorStyles = scorePredictorStyleSheet.locals || {};

const TEAM_IMAGE_WIDTH = 40;
const ONE_HUNDRED = 100;
const DELAY_FOR_BARS_ANIMATION_DESKTOP = 500;
const DELAY_FOR_BARS_ANIMATION_MOBILE = 300;
const SINGLE_DIGIT_MAX = 9;

/**
 * Component for the ResultsScreen
 *
 * @param {object} props prediction option props
 * @param {object} props.scorePredictor scorePredictor option data
 * @param {object} props.fanPrediction fan's prediction data
 * @returns {Function} <ScorePredictorHeader />
 */
function ResultsScreen({ scorePredictor, fanPrediction }) {
    const { t } = useTranslation();
    const { submittingPrediction } = useContext(ScorePredictorContext);
    const isClosed = scorePredictor?.state === 'closed';
    const isResults = scorePredictor?.state === 'resolved';

    const closedNoPrediction = isClosed && !fanPrediction;
    const resultsNoPrediction = isResults && !fanPrediction;

    const [showTop5, setShowTop5] = useState(false);
    const [barPercents, setBarPercents] = useState([]);

    const showSubmitBtnOnMobile =
        !showTop5 && !closedNoPrediction && !resultsNoPrediction;

    // Memoized top 5 options for linter compliance
    const top5Options = useMemo(
        () => scorePredictor?.questions?.[0]?.options || [],
        [scorePredictor]
    );

    const totalPredictions = scorePredictor?.total_submissions || 0;

    const correctPredictions =
        scorePredictor?.questions?.[0]?.correct_options?.[0]?.times_selected;

    const numberOfCorrectFans = correctPredictions || 0;
    const isFanCorrect = isPredictionCorrect(scorePredictor, fanPrediction);

    const onSubmit = () => {
        setShowTop5(true);
    };

    const isDesktop = window.matchMedia('(min-width: 840px)').matches;

    // Animate bars in when top5 is shown
    useEffect(() => {
        if ((showTop5 || isDesktop) && top5Options.length > 0) {
            // Start with 0 width, then animate to percent
            setBarPercents(Array(top5Options.length).fill(0));
            setTimeout(
                () => {
                    setBarPercents(
                        top5Options.map((opt) =>
                            totalPredictions > 0
                                ? (opt.times_selected / totalPredictions) *
                                  ONE_HUNDRED
                                : 0
                        )
                    );
                },
                isDesktop
                    ? DELAY_FOR_BARS_ANIMATION_DESKTOP
                    : DELAY_FOR_BARS_ANIMATION_MOBILE
            );
        }
    }, [showTop5, top5Options, totalPredictions, isDesktop]);

    /**
     * Renders a team image for the results screen, handling standard and external URLs.
     *
     * @param {string} imageUrl - Standard image URL.
     * @param {string} externalImageUrl - External image URL.
     * @param {string} imageText - Alt text for the image.
     * @param {string} [imgClass] - Optional className for the image wrapper.
     * @returns {Element|null} Returns the image element or null if no image is available.
     */
    function renderResultTeamImage(
        imageUrl,
        externalImageUrl,
        imageText,
        imgClass
    ) {
        const wrapperClass = imgClass || styles.resultsTeamImageWrapper;
        if (imageUrl) {
            return (
                <div className={wrapperClass}>
                    <img
                        src={getImageUrl(imageUrl, TEAM_IMAGE_WIDTH)}
                        alt={imageText || ''}
                        className={styles.resultsTeamImage}
                    />
                </div>
            );
        }
        if (externalImageUrl) {
            return (
                <div className={wrapperClass}>
                    <img
                        src={externalImageUrl}
                        alt={imageText || ''}
                        className={styles.resultsTeamImage}
                        onError={onErrorImage}
                    />
                </div>
            );
        }
        return null;
    }

    return (
        <div
            part="score-predictor-results-screen"
            className={scorePredictorStyles.scorePredictorResultsScreeen}
            data-testid="results-screen"
        >
            <style>{styleSheet.toString()}</style>

            {showSubmitBtnOnMobile && (
                <div className={styles.resultsButton}>
                    <SubmitButton
                        onSubmit={onSubmit}
                        title={
                            isResults
                                ? t('scorePredictor.seeResults')
                                : t('scorePredictor.seeTop5')
                        }
                        buttonText={
                            isResults
                                ? t('scorePredictor.seeResults')
                                : t('scorePredictor.seeTop5')
                        }
                        loading={submittingPrediction}
                    />
                </div>
            )}

            {resultsNoPrediction && (
                <div className={styles.resultsBadgeNoPredictionDesktop}>
                    <ResultsBadge
                        correctFans={numberOfCorrectFans}
                        totalFans={totalPredictions}
                        isFanCorrect={fanPrediction ? isFanCorrect : null}
                    />
                </div>
            )}

            <div
                className={classNames(styles.results, {
                    [styles.resultsShow]:
                        showTop5 || closedNoPrediction || resultsNoPrediction
                })}
            >
                {fanPrediction && !isResults && (
                    <div
                        className={styles.resultsHelper}
                        data-testid="results-come-back-later-text"
                    >
                        {t('scorePredictor.comeBackLater')}
                    </div>
                )}

                {closedNoPrediction && (
                    <div
                        className={styles.resultsClosedHelper}
                        data-testid="closed-no-prediction-text"
                    >
                        {t('scorePredictor.predictorClosed')}
                    </div>
                )}

                <div className={styles.resultsPopularLabel}>
                    {t('scorePredictor.mostPopularPredictions')}
                </div>

                <ul className={styles.resultsList}>
                    {top5Options.map((opt, index) => {
                        const percent = barPercents[index] || 0;

                        const isMatch = isFanPredictionMatch(
                            fanPrediction,
                            opt
                        );

                        const isCorrectPrediction = isPredictionCorrect(
                            scorePredictor,
                            opt
                        );

                        const orderNumber = opt.view_order + 1;

                        return (
                            <li key={index} className={styles.resultsItem}>
                                {isCorrectPrediction ? (
                                    <div className={styles.resultsWinnerIcon}>
                                        <WinnerIcon />

                                        <span
                                            className={classNames(
                                                styles.resultsNumber,
                                                styles.resultsNumberInvert,
                                                {
                                                    [styles.resultsNumberMultiDigit]:
                                                        orderNumber >
                                                        SINGLE_DIGIT_MAX
                                                }
                                            )}
                                        >
                                            {orderNumber}
                                        </span>
                                    </div>
                                ) : (
                                    <span className={styles.resultsNumber}>
                                        {index + 1}
                                    </span>
                                )}

                                <div
                                    className={classNames(
                                        styles.resultsItemWrapper,
                                        {
                                            [styles.resultsItemWrapperHighlight]:
                                                isMatch
                                        }
                                    )}
                                >
                                    <div className={styles.resultsScore}>
                                        {renderResultTeamImage(
                                            scorePredictor.entity_a_image_url,
                                            scorePredictor.entity_a_external_image_url,
                                            scorePredictor.entity_a_image_text
                                        )}

                                        <span
                                            className={styles.resultsScoreNums}
                                        >
                                            <span>{opt.entity_a_score}</span>
                                            <span>-</span>
                                            <span>{opt.entity_b_score}</span>
                                        </span>

                                        {renderResultTeamImage(
                                            scorePredictor.entity_b_image_url,
                                            scorePredictor.entity_b_external_image_url,
                                            scorePredictor.entity_b_image_text
                                        )}
                                    </div>
                                    {/* Percentage bar */}
                                    <div
                                        style={{
                                            width: '100%',
                                            height: 4,
                                            background:
                                                'color-mix(in srgb, var(--on-surface) 15%, transparent)',
                                            borderRadius: 1,
                                            margin: '6px 0 5px'
                                        }}
                                    >
                                        <div
                                            style={{
                                                width: `${percent}%`,
                                                height: '100%',
                                                background:
                                                    'color-mix(in srgb, var(--on-surface) 75%, transparent)',
                                                borderRadius: 1,
                                                transition:
                                                    'width 0.7s cubic-bezier(0.4,0,0.2,1)'
                                            }}
                                        />
                                    </div>
                                    <div className={styles.resultsVotes}>
                                        <span
                                            className={
                                                styles.resultsVotesPercentage
                                            }
                                        >
                                            {getVotePercentage(
                                                opt.times_selected,
                                                totalPredictions
                                            )}
                                        </span>
                                        <span
                                            className={
                                                styles.resultsVotesPredictions
                                            }
                                        >
                                            {t('scorePredictor.timesSelected', {
                                                count: opt.times_selected
                                            })}
                                        </span>
                                    </div>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            </div>
        </div>
    );
}

ResultsScreen.propTypes = {
    scorePredictor: PropTypes.object.isRequired,
    fanPrediction: PropTypes.object.isRequired
};

export default ResultsScreen;
